// import 'todomvc-app-css/index.css'
// import './index.css'

import { makePersistedAdapter } from '@livestore/adapter-web'
import LiveStoreSharedWorker from '@livestore/adapter-web/shared-worker?sharedworker'
import { type BootStatus, createStorePromise, liveStoreVersion, queryDb } from '@livestore/livestore'

import LiveStoreWorker from './livestore.worker.ts?worker'
import { events, SyncPayload, schema, tables } from './schema.ts'

type TodoItemData = {
  id: string
  text: string
  completed: boolean
}

export const parseTemplate = (source: string) => {
  const el = document.createElement('template')
  el.innerHTML = source

  return {
    source,
    cloneNode() {
      return el.content.cloneNode(true)
    },
  }
}

// These are here to try to get editors to highlight strings correctly 😔
export const html = (strings: TemplateStringsArray, ...values: unknown[]) =>
  parseTemplate(String.raw({ raw: strings }, ...values))
export const css = (strings: TemplateStringsArray, ...values: unknown[]) => String.raw({ raw: strings }, ...values)

const resetPersistence = import.meta.env.DEV && new URLSearchParams(window.location.search).get('reset') !== null

if (resetPersistence) {
  const searchParams = new URLSearchParams(window.location.search)
  searchParams.delete('reset')
  window.history.replaceState(undefined, '', `${window.location.pathname}?${searchParams.toString()}`)
}

const adapter = makePersistedAdapter({
  storage: { type: 'opfs' },
  worker: LiveStoreWorker,
  sharedWorker: LiveStoreSharedWorker,
  resetPersistence,
})

const syncPayload = { authToken: 'insecure-token-change-me' }

let storeBootStatus: BootStatus = { stage: 'loading' }
const storeBootDoneListeners = new Set<() => void>()

const notifyStoreBootStatus = (status: BootStatus) => {
  storeBootStatus = status

  if (status.stage !== 'done') {
    return
  }

  for (const listener of storeBootDoneListeners) {
    listener()
  }

  storeBootDoneListeners.clear()
}

const onStoreBootDone = (listener: () => void) => {
  if (storeBootStatus.stage === 'done') {
    listener()
    return () => {}
  }

  storeBootDoneListeners.add(listener)

  return () => {
    storeBootDoneListeners.delete(listener)
  }
}

const store = await createStorePromise({
  schema,
  adapter,
  storeId: 'todomvc-custom-elements',
  onBootStatus: notifyStoreBootStatus,
  syncPayloadSchema: SyncPayload,
  syncPayload,
})

// Add version badge
console.log(`LiveStore v${liveStoreVersion}`)
const versionBadge = document.createElement('div')
versionBadge.textContent = `v${liveStoreVersion}`
versionBadge.style.cssText = `
  position: fixed;
  bottom: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  color: white;
  z-index: 1000;
  user-select: none;
`
document.body.appendChild(versionBadge)

const appState$ = queryDb(tables.uiState.get())
const todos$ = queryDb(tables.todos.where({ deletedAt: null }))

const updatedNewTodoText = (text: string) => store.commit(events.uiStateSet({ newTodoText: text }))

const todoCreated = (text: string) =>
  store.commit(events.todoCreated({ id: crypto.randomUUID(), text }), events.uiStateSet({ newTodoText: '' }))

const toggleTodo = (todo: TodoItemData) => {
  if (todo.completed) {
    store.commit(events.todoUncompleted({ id: todo.id }))
  } else {
    store.commit(events.todoCompleted({ id: todo.id }))
  }
}

const todoDeleted = (todo: TodoItemData) => store.commit(events.todoDeleted({ id: todo.id, deletedAt: new Date() }))

const TodoItemTemplate = html`
  <link rel="stylesheet" href="/src/index.css" />
  <li class="relative text-2xl border-b border-b-[#ededed] group">
    <div class="flex">
      <input type="checkbox" class="toggle ml-4" />
      <label
        class="break-words pr-[15px] py-[15px] pl-[30px] block leading-6 transition-colors duration-400 font-normal text-[#484848]"
      ></label>
      <button
        class="hidden absolute top-0 right-[10px] bottom-0 w-[40px] h-[40px] my-auto text-[30px] text-[#949494] transition-colors duration-200 ease-out hover:text-[#C18585] after:content-['x'] group-hover:block"
      ></button>
    </div>
  </li>
`

class TodoItem extends HTMLElement {
  #todo: TodoItemData | null

  constructor() {
    super()
    this.#todo = null
    const shadowRoot = this.attachShadow({ mode: 'open' })
    shadowRoot.append(TodoItemTemplate.cloneNode())

    const button = shadowRoot.querySelector('button')!
    button.addEventListener('click', this.onDelete.bind(this))

    const checkbox = shadowRoot.querySelector('input[type=checkbox]')!
    checkbox.addEventListener('change', this.onToggle.bind(this))
  }

  onDelete() {
    if (this.#todo) {
      todoDeleted(this.#todo)
    }
  }

  onToggle() {
    if (this.#todo) {
      toggleTodo(this.#todo)
    }
  }

  set todo(t: TodoItemData | null) {
    this.#todo = t
    this.updateTemplate()
  }

  get todo(): TodoItemData | null {
    return this.#todo
  }

  updateTemplate() {
    console.debug({ shadowRoot: this.shadowRoot })

    const label = this.shadowRoot!.querySelector('label')
    label!.textContent = this.#todo?.text || ''

    const checkbox = this.shadowRoot!.querySelector('input')
    checkbox!.checked = !!this.#todo?.completed
  }
}

customElements.define('todo-item', TodoItem)

const TodoListTemplate = html`
  <link rel="stylesheet" href="/src/index.css" />
  <header>
    <form>
      <input
        class="relative m-0 w-full text-2xl font-inherit leading-7 text-inherit p-4 pl-[60px] border-none shadow-[inset_0_-2px_1px_0_rgba(0,0,0,0.08)] box-border focus:outline-0 focus:shadow-[0_0_2px_2px_#CF7D7D]"
        autofocus
        placeholder="What needs to be done?"
      />
    </form>
  </header>
  <section class="main">
    <ul class="list-none">
      <slot></slot>
    </ul>
  </section>
`

class TodoList extends HTMLElement {
  constructor() {
    super()
    const shadowRoot = this.attachShadow({ mode: 'open' })
    shadowRoot.append(TodoListTemplate.cloneNode())

    const input = shadowRoot.querySelector('input')
    input?.addEventListener('input', this.onInput.bind(this))

    const form = shadowRoot.querySelector('form')
    form?.addEventListener('submit', this.onSubmit.bind(this))
  }

  onInput(e: Event) {
    const input = e.target as HTMLInputElement
    updatedNewTodoText(input.value)
  }

  onSubmit(e: Event) {
    e.preventDefault()
    const input = this.shadowRoot!.querySelector('input')
    todoCreated(input!.value)
  }

  #todos: ReadonlyArray<Todo> = []

  connectedCallback() {
    const input = this.shadowRoot.querySelector('input')!

    // NOTE: can we get an AsyncIterator for newValues as well?
    // TODO unsubscribe
    store.subscribe(todos$, (newValue) => {
      this.#todos = newValue
      this.updateTodoItems()
    })

    // TODO unsubscribe
    store.subscribe(appState$, (newValue) => {
      input.value = newValue.newTodoText
    })

    const markStoreReady = () => {
      if (this.dataset.storeReady === 'true') {
        return
      }

      /**
       * Boot completion is the closest lifecycle boundary we have to "persistence rehydration is settled".
       * We do one post-boot app-state read before exposing `data-store-ready` so tests don't race an
       * early empty snapshot during reloads.
       *
       * TODO expose a first-class hydration-complete signal from LiveStore so examples don't need this latch.
       */
      let unsubscribeReadyCheck: (() => void) | undefined
      unsubscribeReadyCheck = store.subscribe(appState$, (newValue) => {
        input.value = newValue.newTodoText
        this.dataset.storeReady = 'true'
        queueMicrotask(() => unsubscribeReadyCheck?.())
      })
    }

    // TODO unsubscribe
    onStoreBootDone(markStoreReady)
  }

  updateTodoItems() {
    // TODO: don't clear, just update existing or add/remove
    this.replaceChildren()

    for (const todo of this.#todos) {
      const todoEl = document.createElement('todo-item') as TodoItem
      todoEl.todo = todo
      this.append(todoEl)
    }
  }
}

customElements.define('todo-list', TodoList)
